import axios from 'axios';
import { AirportAutocomplete, FlightSearchResponse } from 'ts/flight/interfaces/FlightSearch';
import { SearchFlightModel } from 'ts/flight/classes/SearchFlightModel';
import { FlightDetailsResponse } from 'ts/flight/interfaces/FlightDetailsResponse';
import {
  BookingModel,
  FlightAirlines,
  FlightBookCompleteResponse,
  FlightBookResponse,
  FlightRulesDisplay,
  StaticDataResponse,
} from 'ts/flight/interfaces/FlightBookResponse';
import { Screenshot } from 'utils/utils';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import { SubmitScreenshotsParams } from 'ts/shared/interfaces/Generic';

interface GetFlightsParams {
  body: {
    searchToken: string;
    skip: number;
  };
  controller: AbortController;
}

interface GetFlightParams {
  searchToken: string;
  id: string;
  pairId?: string;
  isSale?: boolean;
}

export const getFlightSearch = async (
  term: string,
  controller: AbortController,
): Promise<AirportAutocomplete[] | undefined> => {
  try {
    const url = 'Flights/Autocomplete';
    const language = localStorage.getItem('language') || 'he';
    return (
      await axios.post(url, undefined, { params: { term, language }, signal: controller?.signal })
    ).data.airports;
  } catch (err) {
    return undefined;
  }
};

export const beginFlightSearch = async (
  params: SearchFlightModel,
  controller: AbortController,
): Promise<string> => {
  try {
    let paramsToRequest = [];
    let flightSources = [];
    if (params.isNDC) {
      flightSources.push('NDCALL');
    }
    if (params.isLowCost) {
      flightSources.push('LOWALL');
    }
    let constParams: any = {
      NumberOfAdults: params.numberOfAdults,
      NumberOfChildren: params.numberOfChildren,
      NumberOfYouths: params.numberOfYouths,
      NumberOfSeniors: params.numberOfSeniors,
      NumberOfInfants: params.numberOfInfants,
      CabineType: params.cabineType,
      SearchType: parseInt(params.searchType),
      NumberOfBags: params.numberOfBags,
      ArrivalDate: params.arrivalDate,
      DirectFlight: params.directFlight,
      RecaptchaToken: params.recaptchaToken,
      FlightSources: flightSources,
    };

    if (params.skipHistorySave) {
      constParams.SkipHistorySave = true;
    }

    const departureDateArr = params.departureDate.split('|');
    const departureDescArr = params.departureDesc.split('|');
    const departureCodeArr = params.departureCode.split('|');
    const arrivalDescArr = params.arrivalDesc.split('|');
    const arrivalCodeArr = params.arrivalCode.split('|');

    for (let i = 0; i < departureDescArr.length; i++) {
      const paramsObj = {
        ...constParams,
        DepartureDesc: departureDescArr[i],
        DepartureCode: departureCodeArr[i],
        ArrivaleDesc: arrivalDescArr[i],
        ArrivalCode: arrivalCodeArr[i],
        DepartureDate: departureDateArr[i] || departureDateArr[0],
      };
      paramsToRequest.push(paramsObj);
    }

    if (
      params.additionalFrom &&
      params.additionalFrom !== params.departureCode &&
      params.searchType === '1'
    ) {
      const paramsObj = {
        ...constParams,
        DepartureDesc: params.additionalFromLabel,
        DepartureCode: params.additionalFrom,
        ArrivaleDesc: params.additionalToLabel,
        ArrivalCode: params.additionalTo,
        DepartureDate: params.departureDate,
      };
      paramsToRequest.push(paramsObj);
    }

    const url = 'Flights/BeginFlightSearch';

    const data = (await axios.post(url, paramsToRequest, { signal: controller.signal })).data;
    sessionStorage.setItem('searchTokenFlights', data.searchToken);

    return data.searchToken;
  } catch (err) {
    return '';
  }
};

export const getFlights = async (
  params: GetFlightsParams,
): Promise<FlightSearchResponse | undefined> => {
  try {
    const url = 'Flights/GetFlights';

    return (await axios.post(url, params.body, { signal: params.controller.signal })).data;
  } catch (err) {
    return undefined;
  }
};

export const getFlight = async (
  params: GetFlightParams,
): Promise<FlightDetailsResponse | undefined> => {
  try {
    const url = 'Flights/Details';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const getBook = async (params: GetFlightParams): Promise<FlightBookResponse | undefined> => {
  try {
    const language = localStorage.getItem('language') || 'he';
    const url = 'Flights/Book';

    return (
      await axios.post(url, { ...params, isSale: params.isSale ? true : undefined, language })
    ).data;
  } catch (err) {
    return undefined;
  }
};

export const getStaticData = async (): Promise<StaticDataResponse | undefined> => {
  try {
    const language = localStorage.getItem('language') || 'he';
    const url = 'Flights/GeStaticData';

    const response = (await axios.post(url, { language })).data;

    const airlines = response.flightAirlines.map((airline: FlightAirlines) => ({
      label: airline.text,
      value: airline.value,
    }));

    return {
      bookingAges: response.bookingAges,
      flightAirlines: airlines,
    };
  } catch (err) {
    return undefined;
  }
};

export const getRules = async (
  params: GetFlightParams,
): Promise<FlightRulesDisplay[] | undefined> => {
  try {
    const url = 'Flights/RuleDisplay';

    return (await axios.post(url, params)).data.ruleDsiplay.flightRulesDisplay;
  } catch (err) {
    return undefined;
  }
};

export const sendRequest = async (
  body: BookingModel,
): Promise<FlightBookCompleteResponse | undefined> => {
  try {
    const screenshotsJson = sessionStorage.getItem('screenshotsFlights');
    let screenshots: string[] = [];
    if (screenshotsJson) {
      const screenshotsArr: Screenshot[] = JSON.parse(screenshotsJson);
      screenshots = screenshotsArr.map((screenShots) => screenShots.data);
    }

    const language = 1;
    const url = 'Flights/BookComplete';

    return (await axios.post(url, { ...body, language, screenshots })).data;
  } catch (err) {
    return undefined;
  }
};

export const sendMultiRequest = async (
  body: BookingModel,
): Promise<FlightBookCompleteResponse | undefined> => {
  try {
    const url = 'Flights/BookCompleteMulti';

    return (await axios.post(url, body)).data;
  } catch (err) {
    return undefined;
  }
};

export const checkPaymentStatus = async (transactionId: string): Promise<any | undefined> => {
  try {
    const url = 'Flights/CheckPaymentStatus';

    return (await axios.post(url, { transactionId })).data;
  } catch (err) {
    return undefined;
  }
};

export const submitScreenshots = async (params: SubmitScreenshotsParams): Promise<any> => {
  try {
    const url = 'Flights/SubmitScreenshots';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

interface UpdatePaymentBlockParams {
  sourcePrice: number;
  packageCurrency: string;
  addPrice: number;
  searchToken: string;
  lang: string;
}

export const updatePaymentBlock = async (params: UpdatePaymentBlockParams) => {
  try {
    const url = 'Utils/UpdatePaymentBlock';
    return (await axios.post(url, undefined, { params })).data;
  } catch (err) {
    return undefined;
  }
};

interface GetFamilyPlanParams {
  searchToken: string;
  id: string;
  getFamilyPlan: boolean;
}
export const getBaggagePrice = async (params: GetFamilyPlanParams) => {
  try {
    const url = 'Flights/GetBaggagePrice';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

interface AnyPageParams {
  products: string;
  category: string;
  systemProductsDetails: string;
}

export const getAnyPage = async (params: AnyPageParams): Promise<CmsPageResponse | undefined> => {
  try {
    const url = 'data/AnyPage';
    return (await axios.post<CmsPageResponse>(url, { ...params, language: 'he' })).data;
  } catch (err) {
    return undefined;
  }
};
export interface SearchDate {
  date: Date;
  minPrice: number;
}
interface GetDatesByCityParams {
  cityCode: string;
  recaptchaToken: string;
  flightSourcesDefault?: string;
  isBackDates?: boolean;
  checkIn?: string;
}
export const getDatesByCity = async (
  params: GetDatesByCityParams,
): Promise<SearchDate[] | undefined> => {
  try {
    const url = 'ILPackages/GetDatesByCity';
    return (await axios.post(url, { ...params, type: 4 })).data.dates
      .map((response: any) => {
        return {
          date: new Date(response.date),
          minPrice: response.minPrice,
        };
      })
      .sort((a: SearchDate, b: SearchDate) => a.date.getTime() - b.date.getTime());
  } catch (err) {
    return undefined;
  }
};
